import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import { treeVideoStatusSchema } from '../enums/tree-video-status.enum.js'

/**
 * A video of one tree or one irrigation record, or a stock clip.
 *
 * Playback fields are null until Stream has finished transcoding, and stay null
 * for a video that failed. A client should branch on `status` rather than on
 * whether a URL happens to be present — the two agree, but only one of them
 * says why.
 */
export const treeVideoDtoSchema = z.object({
  id: uuidSchema,
  /**
   * What the video is of: at most one of these is set (`VIDEO_OWNER_KEYS`).
   * None is a stock clip explaining a kind of problem.
   */
  treeId: uuidSchema.nullable(),
  waterSourceId: uuidSchema.nullable(),
  irrigationTimerId: uuidSchema.nullable(),
  irrigationEquipmentId: uuidSchema.nullable(),
  irrigationProblemId: uuidSchema.nullable(),
  status: treeVideoStatusSchema,
  fileName: z.string().nullable(),
  durationSeconds: z.number().int().nonnegative().nullable(),
  /** What Stream said when it refused the file. Null unless ERRORED. */
  errorMessage: z.string().nullable(),

  /**
   * A still from the video, and the player to open it in.
   *
   * Both carry a signed token when Stream signing is configured, and both
   * expire — which is why they are computed per request rather than stored.
   */
  thumbnailUrl: z.string().url().nullable(),
  playbackUrl: z.string().url().nullable(),

  uploadedByName: z.string().nullable(),
  createdAt: z.string().datetime(),
})

export type TreeVideoDto = z.infer<typeof treeVideoDtoSchema>

export const treeVideoListDtoSchema = z.object({
  videos: z.array(treeVideoDtoSchema),
})

export type TreeVideoListDto = z.infer<typeof treeVideoListDtoSchema>

/**
 * Whether this deployment can take video. Its own response so a survey can
 * hide the control instead of offering a button that answers 503.
 */
export const treeVideoAvailabilityDtoSchema = z.object({
  enabled: z.boolean(),
})

export type TreeVideoAvailabilityDto = z.infer<
  typeof treeVideoAvailabilityDtoSchema
>

/**
 * Everything the browser needs to send one video straight to Stream.
 *
 * The same shape as a document's upload target, and for the same reason: a
 * 200 MB clip has no business passing through the API process.
 */
export const treeVideoUploadTargetDtoSchema = z.object({
  video: treeVideoDtoSchema,
  /** One-time URL to POST the file to. Issued by Stream, expires quickly. */
  uploadUrl: z.string().url(),
})

export type TreeVideoUploadTargetDto = z.infer<
  typeof treeVideoUploadTargetDtoSchema
>
